import React, {useCallback, useEffect, useRef, useState} from 'react';
import {history, connect} from 'umi';
import { Layout, Card, Justify, SearchBox, Button, Bubble, notification } from 'tea-component';

import AbnormalPage from '@/pages/datalab/components/AbnormalPage';
import DataTable from './components/DataTable';

import { getNewCategory } from '@dbl/services/dataSource';
import {DATALAB_EDITOR, SYSTEM_NAME} from '@dbl/core/global';
import {updateUrlParams, useDblIntl} from '@/pages/datalab/utils/utils';
import {buriedPoint} from "@/pages/home/utils/buriedPoint";
import FilterTabs from '@/pages/datalab/pages/Number/DataManagement/components/FilterTabs';
import SvgIcon from "@/pages/home/components/SvgIcon";
import { namespace } from "@/pages/datalab/pages/Number/DataManagement/model";
import {useScroll} from 'ahooks';
import { AuthPermissionDisablesContainers } from '../../WorkBench/MyDashboard';
import './index.scss';

/**
 * 有以下状态要保存在路径中, 用户回退时, 恢复页面之前的状态
 * 1. Tag切换: 一级类目, 二级类目, 表周期
 * 2. 搜索
 * 3. 表格排序
 * 4. 表格每页条数
 * 5. 表格当前页数
 *
 *  从左侧目录进入页面, 展示默认状态
 */

const { Content } = Layout;
function RefreshBtn(props: any) {
  const { dataFormatMessage, refreshLoading, dispatch, refreshStatus } = props;
  
  useEffect(() => {
    
    switch (refreshStatus) {
      case 'success': {
        // 弹出刷新成功提示
        notification.success({
          description: dataFormatMessage('refresh_success'),
        });
        dispatch({type: `${namespace}/changeRefreshStatus`, payload: 'normal'});
        return;
      }
      case 'error': {
        notification.error({
          description: dataFormatMessage('refresh_error'),
        });
        dispatch({type: `${namespace}/changeRefreshStatus`, payload: 'normal'});
        return;
      }
      default: {}
    }
  }, [refreshStatus]);
  
  return (<Bubble
    dark
    placement="left"
    content={dataFormatMessage('refresh_bubble')}
  >
    <Button
      className={'ns-btn--border btn-top'}
      loading={refreshLoading}
      onClick={() => {
        dispatch({type: `${namespace}/refreshTable`});
      }}
    >
      <SvgIcon className='btn-top-icon' type="refresh" />
      &nbsp;&nbsp;
      {dataFormatMessage('refresh_btn')}
    </Button>
  </Bubble>);
}

function SheetManagement(props: any) {
  
  const query = history.location.query;
  
  const { dblFormatMessage: dataFormatMessage} = useDblIntl("data_table");
  const stickyRef = useRef(null);
  const inViewport = useScroll(stickyRef) as { "left": number; "top": number; };
  const hasSpecifiedPrivilege = useCallback((privilege: string) => props?.allPrivileges?.includes(privilege), [props.allPrivileges]);

  useEffect(() => {
    buriedPoint({
      pageId: '600401',
      pageName: '数据表管理',
      pagePath: '/DataManagement/SheetManagement',
      extInfo: `{"pageName": "Tables", "pageNameCn": "数据表管理", "id": ""}`,
      uid: props.gameCode,
      typeId: 'datalab_pc_v2',
      menuId: '0',
    });
  }, [props.gameCode]);
  
  // 搜索框点击搜索按钮后, 触发表格请求的搜索字段
  const [searchWords, setSearchWords] = useState<string | undefined>(query?.keywords ?? '');
  const [selectTables, setSelectTables] = useState<string[]>([]);
  // 搜索框受控value
  const [searchValue, setSearchValue] = useState<string | undefined>(query?.keywords ?? '');
  const [categories, setCategories] = useState({}); // 后端返回的类别数据
  const [filterValue, setFilterValue] = useState({}); // 类别筛选的值
  const [pageStatus, setPageStatus] = useState('loading');
  const ceilingThreshold=inViewport?.top > 400;
  
  // 请求类目信息
  useEffect(() => {
    if (props.refreshStatus !== 'normal') return;
    setPageStatus('loading');
    setFilterValue(JSON.parse(query?.filterValue || '{ "cycle": ["all"], "label": ["all"], "sub_label": ["all"] }')); // 触发表格数据重新请求
    getNewCategory({ game_code: props.gameCode })
      .then((res) => {
        setCategories(res);
        setPageStatus('normal');
      })
      .catch((e) => {
        setPageStatus('error');
        console.log('类目请求错误: ', e);
      });
    setSelectTables([]);
  }, [props.gameCode, props.refreshStatus]);
  return (
    <Content >
      <Content.Header
        style={{
          display: ceilingThreshold ? 'none':'inline'
        }}
        title={dataFormatMessage('title')}
        operation={ <RefreshBtn dataFormatMessage={dataFormatMessage} {...props}/> }
      />
      <Content.Body full className="data-management"  ref={stickyRef}
        style={{
          padding: ceilingThreshold ? '0px 24px':'20px 24px'
        }}
      >
        <Justify
          className="data-management-buttom"
          left={  
            <AuthPermissionDisablesContainers {
              ...{
                type: DATALAB_EDITOR.AddChartBtn,
                customDisabled: selectTables.length < 1,
              }
            }>
              <Button
                type="primary"
                onClick={() => {
                  history.push(
                    `/${SYSTEM_NAME}/${props.gameCode}/Analysis/MultiAnalysis?tableId=${selectTables.map(item => item?.table_id).join(',')}&autoSelect=1`,
                  );
                }}
              >
                {dataFormatMessage('enter_analysis')}
              </Button>
            </AuthPermissionDisablesContainers>
          }
          right={
            <SearchBox
              className="data-management-search"
              value={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                if (!value) {
                  setSearchWords('');
                }
              }}
              onSearch={(keyword) => {
                if(searchWords === keyword) return;
                setSearchWords(keyword);
                if (keyword?.length !== 0) {
                  setFilterValue({ "cycle": ["all"], "label": ["all"], "sub_label": ["all"] });
                  // 搜索时重置排序为无，不传 order_by
                  updateUrlParams({
                    searchValue: keyword,
                    sort: {},
                    // quickValue: { drivers: [], db_name_filters: [] },
                    order_by: ''
                  });
                } else {
                  updateUrlParams({
                    searchValue: keyword,
                    sort: { by: 'use_count', order: 'desc' },
                    order_by: 'use_count',
                  });
                }
              }}
              onClear={() => {
                setSearchWords('');
                updateUrlParams({
                  searchValue: '',
                  sort: { by: 'use_count', order: 'desc' },
                  order_by: 'use_count',
                });
              }}
              placeholder={dataFormatMessage('search')}
            />
          }
        />
        <Card>
          <Card.Body className="add-Source">
            <AbnormalPage status={pageStatus}>
              <FilterTabs categories={categories} filterValue={filterValue} setFilterValue={setFilterValue} />
              <DataTable
                className='dbi-table-sticky'
                gameCode={props.gameCode}
                searchWords={searchWords}
                selectTables={selectTables}
                setSelectTables={setSelectTables}
                clearSearchWords={() => {
                  setSearchWords('');
                  setSearchValue('');
                  updateUrlParams({
                    searchValue: '',
                    sort: { by: 'use_count', order: 'desc' },
                    order_by: 'use_count',
                  });
                }}
                filterValue={filterValue}
                translatorMap={categories?.trans || {}}
                hasSpecifiedPrivilege={hasSpecifiedPrivilege}
              />
            </AbnormalPage>
          </Card.Body>
        </Card>
      </Content.Body>
    </Content>
  );
}
export default connect((state: any) => ({
  refreshLoading: state.loading.effects[`${namespace}/refreshTable`],
  refreshStatus: state[namespace].refreshStatus,
  allPrivileges: state.privilegesConfig.allPrivileges,
  gameCode: state.datalab.gameCode,
}))(SheetManagement);
